import { request } from "@/Api"
import { CustomRow } from "@/table/CustomTable"
import { Autocomplete, Checkbox, Chip, Collapse, Link, TableCell, TableRow, TextField, Tooltip } from "@mui/material"
import AccountingBillActions from "./AccountingBillActions"
import { memo, useEffect, useState } from "react"
import { formattedCurrency } from "@/utils"
import { useSelector } from "react-redux"
import { checkBill, selectBillIsChecked, unCheckBill } from "./accountingBillsSlice"
import { ACCOUNTING_BILL_STATUS_ALLOCATED, ACCOUNTING_BILL_STATUS_PAID } from "@/actuals/constants"
import { useDispatch } from "react-redux"
import { setError } from "@/nav/navSlice"
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

const AccountingBill = ({ billData, categories, taxCodes, isLine, hidden }) => {
    const dispatch = useDispatch()
    const [ bill, setBill ] = useState(billData)
    const [ expanded, setExpanded ] = useState(false)
    const selected = useSelector((state) => selectBillIsChecked(state, bill.id))
    const checkboxAvailable = (bill.status && bill.status != ACCOUNTING_BILL_STATUS_PAID) && !isLine
    const canSync = checkboxAvailable && bill.bill_lines.every((line) => line.status == ACCOUNTING_BILL_STATUS_ALLOCATED) || bill.status != ACCOUNTING_BILL_STATUS_ALLOCATED
    const mainPaid = !isLine && bill.bill_lines.length > 0 && bill.status == ACCOUNTING_BILL_STATUS_PAID
    const handleCheck = (event, isChecked) => {
        if (isChecked) {
            dispatch(checkBill(bill))
        } else {
            dispatch(unCheckBill(bill))
        }
    }
    const handleAutocompleteChange = (field) => (event, obj) => {
        const updatedBill = {
            ...bill,
            [field]: obj.id,
        }
        setBill(updatedBill)
        request.patch(`/accounting-bills/${bill.id}/`, {
            [field]: obj.id,
        })
        .then(response => {
            dispatch(setError({message: "Bill updated", severity: "success"}))
        })
        .catch(err => {
            dispatch(setError({message: JSON.stringify(err), severity: "error"}))
        })
    }
    const handleNoteChange = (event) => {
        const updatedBill = {
            ...bill,
            description: event.target.value,
        }
        setBill(updatedBill)
        request.patch(`/accounting-bills/${bill.id}/`, {
            "description": event.target.value,
        })
        .then(response => {
            dispatch(setError({message: "Bill Note updated", severity: "success"}))
        })
        .catch(err => {
            dispatch(setError({message: JSON.stringify(err), severity: "error"}))
        })
    }
    const handleExpand = () => {
        setExpanded(!expanded)
    }
    return (
        <>
            <CustomRow style={{display: hidden ? "none" : "table-row"}}> 
                <TableCell align="left" width={50}>
                    {(checkboxAvailable) &&
                    <Tooltip title={canSync ? "" : "Producer didn't approve all bill lines via allocation"}>
                        <span>
                            <Checkbox
                                disabled={!canSync}
                                checked={selected}
                                onChange={handleCheck}
                            />
                        </span>
                    </Tooltip>}
                    {mainPaid && <CheckCircleOutlineRoundedIcon />}
                </TableCell>
                <TableCell align="left" sx={{minWidth: 200}}>
                    <Link href={`/vendors/${bill.vendor_id}`}>{bill.vendor_name_property}</Link>
                </TableCell>
                <TableCell align="left" sx={{minWidth: 200}}>
                    {bill.project_id && <Link href={`/projects/${bill.project_id}/actuals`}>{bill.project_name}</Link>}
                </TableCell>
                <TableCell component="th" scope="row">{bill.accounting_bill_number}</TableCell>
                <TableCell align="left">
                    <Chip
                        variant="outlined"
                        label={bill.status || "unmatched"}
                    />
                </TableCell>
                <TableCell align="right">{formattedCurrency(bill.actual, bill.currency, false, 2)}</TableCell>
                <TableCell align="right">{formattedCurrency(bill.actual_project, bill.project_currency, false, 2)}</TableCell>
                <TableCell align="left" width={300}>
                    <TextField
                        sx={{minWidth: 200}}
                        fullWidth
                        size="small"
                        multiline
                        maxRows={2}
                        defaultValue={bill.description}
                        onBlur={handleNoteChange}
                    />
                </TableCell>
                <TableCell align="left" width={300}>
                    <div style={{width: "100%"}}>
                    <Autocomplete
                        sx={{minWidth: 200}}
                        size="small"
                        defaultValue={bill.accounting_account}
                        options={categories || []}
                        renderInput={(params) => <TextField fullWidth {...params} label="Category"/>}
                        onChange={handleAutocompleteChange("accounting_account_id")}
                        getOptionLabel={(option) => {
                            return option ? option.name : ""
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (!option || !value) {
                                return true
                            }
                            return option.id == value.id
                        }}
                    />
                    </div>
                </TableCell>
                <TableCell align="left" width={300}>
                    <div style={{width: "100%"}}>
                    <Autocomplete
                        sx={{minWidth: 200}}
                        size="small"
                        defaultValue={bill.accounting_tax_code}
                        options={taxCodes || []}
                        renderInput={(params) => <TextField fullWidth {...params} label="Tax Code"/>}
                        onChange={handleAutocompleteChange("accounting_tax_code_id")}
                        getOptionLabel={(option) => {
                            return option ? option.name : ""
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (!option || !value) {
                                return true
                            }
                            return option.id == value.id
                        }}
                    />
                    </div>
                </TableCell>
                <TableCell align="left">
                    <div style={{width: 100}}>{bill.legal_entity_name}</div>
                </TableCell>
                <TableCell align="right">
                    <AccountingBillActions
                        bill={bill}
                        onExpand={(!isLine && bill.bill_lines.length > 0) && handleExpand}
                    />
                </TableCell>
            </CustomRow>

            <>
                {bill.bill_lines.map((line, index) =>
                    <AccountingBill 
                        key={index}
                        billData={line} categories={categories} 
                        onCheck={onCheck} isLine
                        hidden={(bill.bill_lines.length > 0) && !expanded}
                    />
                )}
            </>
        </>
    )
}

export default AccountingBill